//eslint-disable-next-line
//@ts-nocheck

import {
  CustomIndicator,
  PineJS,
  RawStudyMetaInfoId,
  StudyInputType,
  StudyPlotType,
  StudyTargetPriceScale,
} from "@/lib/datafeed/tvTypes.ts";

import { LineStudyPlotStyle } from "@/lib/datafeed/utils.ts";

import { AccessCheck } from "@/lib/indicators/indicators-common-functions";

import { IIndicatorOptions } from "@/domain/interfaces/general.interface";

export const volumePriceTrend = (
  PineJS: PineJS,
  options?: IIndicatorOptions,
): CustomIndicator => {
  return {
    name: "Volume Price Trend",
    metainfo: {
      _metainfoVersion: 51,
      id: "volume-price-trend@tv-basicstudies-1" as RawStudyMetaInfoId,
      description: "[SFM] Volume Price Trend",
      shortDescription: "[SF] VPT",
      is_hidden_study: false,
      is_price_study: true,
      isCustomIndicator: true,
      linkedToSeries: false,
      format: {
        type: "price",
        precision: 2,
      },
      plots: [
        {
          id: "vptPlot",
          type: StudyPlotType.Line,
        },
        {
          id: "vptColorer",
          type: StudyPlotType.Colorer,
          target: "vptPlot",
          palette: "vptPalette",
        },
        {
          id: "backgroundPlot",
          type: StudyPlotType.BgColorer,
          palette: "backgroundPalette",
        },
      ],
      palettes: {
        vptPalette: {
          valToIndex: {
            0: 0,
            1: 1,
          },
          colors: {
            0: { name: "Negative" },
            1: { name: "Positive" },
          },
        },
        backgroundPalette: {
          valToIndex: {
            0: 0,
            1: 1,
          },
          colors: {
            0: { name: "Negative" },
            1: { name: "Positive" },
          },
        },
      },
      defaults: {
        palettes: {
          vptPalette: {
            colors: {
              0: { color: "rgb(178, 24, 44)", width: 1, style: 0 },
              1: { color: "rgb(60, 166, 75)", width: 1, style: 0 },
            },
          },
          backgroundPalette: {
            colors: {
              0: { color: "rgba(178, 24, 44, 0.2)", width: 1, style: 0 },
              1: { color: "rgba(60, 166, 75, 0.2)", width: 1, style: 0 },
            },
          },
        },
        styles: {
          vptPlot: {
            linestyle: 0,
            visible: true,
            plottype: LineStudyPlotStyle.StepLine,
            linewidth: 1,
            trackPrice: false,
            color: "rgb(255, 255, 255)",
          },
        },
        precision: 2,
        inputs: {
          windowLengthInput: 100,
          smoothLengthInput: 100,
        },
      },
      styles: {
        vptPlot: {
          title: "VPT",
          histogramBase: 0,
        },
        backgroundPlot: {
          title: "Background",
          histogramBase: 0,
        },
      },
      inputs: [
        {
          id: "windowLengthInput",
          name: "Window length",
          defval: 100,
          type: StudyInputType.Float,
        },
        {
          id: "smoothLengthInput",
          name: "Smooth length",
          defval: 100,
          type: StudyInputType.Float,
        },
      ],
    },

    constructor: function () {
      this.init = function (context, inputCallback) {
        this._context = context;
        this._input = inputCallback;
      };

      this.main = function (context, inputCallback) {
        this._context = context;
        this._input = inputCallback;

        // Inputs
        const [windowLengthInput, smoothLengthInput]: [number, number] = [
          this._input(0),
          this._input(1),
        ];

        // Markte data parsing
        const high: number = PineJS.Std.high(this._context);
        const low: number = PineJS.Std.low(this._context);
        const open: number = PineJS.Std.open(this._context);
        const close: number = PineJS.Std.close(this._context);
        const volume: number = PineJS.Std.volume(this._context);

        // Calculations
        const highLow: number = (high - low) * 100;
        const openClose: number = (close - open) * 100;
        const vol: number = volume / highLow;
        const spreadVol: number = openClose * vol;
        const spreadVolSeries: any = this._context.new_var(spreadVol);
        const vpt: number =
          spreadVol + PineJS.Std.cum(spreadVolSeries, this._context);
        const highLowDiff: any = this._context.new_var(high - low);

        const priceSpread: number = PineJS.Std.stdev(
          highLowDiff,
          windowLengthInput,
          this._context,
        );

        const v: number =
          spreadVol + PineJS.Std.cum(spreadVolSeries, this._context);
        const vSeries: any = this._context.new_var(v);
        const smooth: number = PineJS.Std.sma(
          vSeries,
          smoothLengthInput,
          this._context,
        );
        const vMinusSmooth: any = this._context.new_var(v - smooth);
        const vSpread: number = PineJS.Std.stdev(
          vMinusSmooth,
          windowLengthInput,
          this._context,
        );

        const shadow: number = ((v - smooth) / vSpread) * priceSpread;
        const out: number = shadow > 0 ? high + shadow : low + shadow;

        const color: number = shadow > 0 ? 1 : 0;

        // Return data
        return [out, color, color, color];
      };
    },
  };
};
